import React, { useEffect, useRef } from 'react';

/**
 * RegimeOverlay — thin absolute-positioned canvas that paints
 * background bands over the chart, one color per regime segment.
 * Consumes `segments` from /api/crypto/intel/regime.
 */
const COLORS = {
  trending_up:   'rgba(0,255,136,0.10)',
  trending_down: 'rgba(255,51,102,0.10)',
  range:         'rgba(247,201,72,0.08)',
  high_vol:      'rgba(255,122,198,0.10)',
  low_vol:       'rgba(0,229,255,0.08)',
  transition:    'rgba(255,255,255,0.04)',
  unknown:       'rgba(255,255,255,0.03)',
};

export default function RegimeOverlay({ engine, segments = [] }) {
  const cnvRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!engine?.provider?.chart) return;
    const chart = engine.provider.chart;
    const draw = () => {
      const cnv = cnvRef.current;
      const wrap = wrapRef.current;
      if (!cnv || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      cnv.width = rect.width * dpr;
      cnv.height = rect.height * dpr;
      cnv.style.width = `${rect.width}px`;
      cnv.style.height = `${rect.height}px`;
      const ctx = cnv.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);
      const ts = chart.timeScale();
      for (const seg of segments) {
        const x1 = ts.timeToCoordinate(seg.from_ts);
        const x2 = ts.timeToCoordinate(seg.to_ts);
        if (x1 == null || x2 == null) continue;
        ctx.fillStyle = COLORS[seg.regime] || COLORS.unknown;
        ctx.fillRect(Math.min(x1, x2), 0, Math.abs(x2 - x1), rect.height);
      }
    };
    draw();
    chart.timeScale().subscribeVisibleTimeRangeChange(draw);
    const obs = new ResizeObserver(draw);
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => {
      try { chart.timeScale().unsubscribeVisibleTimeRangeChange(draw); } catch { /* noop */ }
      obs.disconnect();
    };
  }, [engine, segments]);

  return (
    <div ref={wrapRef} className="absolute inset-0 z-10 pointer-events-none" data-testid="crypto-regime-overlay">
      <canvas ref={cnvRef} />
    </div>
  );
}
